import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as THREE from 'three';
import { useI18n } from '../i18n';
import {
  type GeoPoint,
  type ScreenPos,
 
  type WorldPos,
  type WorldVectorXZ,
  worldToGeodetic,
  segmentDistance,
  totalLength,
  totalLengthWorld3D,
  polygonAreaXZ,
  latLonToMTM8,
  projectToScreen,
  screenToWorldOnPlane,
  fmtDist,
  fmtArea,
  fmtUTM,
  markerColor,
  markerLabel,
  lineMetricsWorld,
  projectPointToLineXZ,
  vectorXZ,
  normalizeXZ,
  cameraForwardXZ,
  perpendicularXZ,
  worldDistanceXZ,
  worldDistance3D,
} from '../utils/measureUtils';



interface MeasureToolProps {
  viewerRef: React.MutableRefObject<any>;
  isActive: boolean;
  onClose: () => void;
  onSave?: (data: { points: GeoPoint[]; mode: string; length: number | null; area: number | null }) => void;
  onPointsChange?: (data: { points: Array<{ x: number; y: number; z: number }>; mode: string; length: number | null; area: number | null }) => void;
  anchorRef?: React.RefObject<HTMLButtonElement>;
  /** Pre-populate with world-space corners (e.g. from SI dimension detection). Tool opens in 'done' state so user can drag-correct. */
  initialPoints?: WorldPos[];
}

function pickWorldPointFromPointer(
  viewer: any,
  canvas: HTMLCanvasElement,
  clientX: number,
  clientY: number
): WorldPos | null {
  const camera = viewer?.camera;
  const raycaster = viewer?.raycaster;
  const splatMesh = viewer?.splatMesh;
  const getRenderDimensions = viewer?.getRenderDimensions;

  if (!camera || !raycaster || !splatMesh || !getRenderDimensions) {
    return null;
  }

  const rect = canvas.getBoundingClientRect();
  const renderDimensions = new THREE.Vector2();
  const outHits: unknown[] = [];

  getRenderDimensions.call(viewer, renderDimensions);
  camera.updateMatrixWorld?.();

  const screenPosition = new THREE.Vector2(
    ((clientX - rect.left) / rect.width) * renderDimensions.x,
    ((clientY - rect.top) / rect.height) * renderDimensions.y
  );

  raycaster.setFromCameraAndScreenPosition(camera, screenPosition, renderDimensions);
  const returnValue = raycaster.intersectSplatMesh(splatMesh, outHits);
  const hits = Array.isArray(returnValue) ? returnValue : outHits;
  const origin = (hits[0] as any)?.origin;

  const x = Number(origin?.x);
  const y = Number(origin?.y);
  const z = Number(origin?.z);

  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return null;
  }

  return { x, y, z };
}

function buildMeasuredPoint(
  worldPos: WorldPos,
  observerCoordinates?: [number, number, number] | null
): GeoPoint {
  if (observerCoordinates && observerCoordinates.length >= 3) {
    return {
      ...worldToGeodetic(worldPos, observerCoordinates),
      worldPos,
      coordinateMode: 'geodetic',
    };
  }

  return {
    latitude: worldPos.z,
    longitude: worldPos.x,
    altitude: worldPos.y,
    worldPos,
    coordinateMode: 'world',
  };
}


type MeasureStep = 'idle' | 'capturing' | 'done';


function buildGuideLine(
  pivot: WorldPos,
  dir: WorldVectorXZ,
  startOffset: number,
  endOffset: number
): { start: WorldPos; end: WorldPos } {
  return {
    start: {
      x: pivot.x - dir.x * startOffset,
      y: pivot.y,
      z: pivot.z - dir.z * startOffset,
    },
    end: {
      x: pivot.x + dir.x * endOffset,
      y: pivot.y,
      z: pivot.z + dir.z * endOffset,
    },
  };
}

function ensureGuideCoversProjection(
  pivot: WorldPos,
  target: WorldPos,
  dir: WorldVectorXZ,
  startOffset: number,
  endOffset: number,
  padding = 1.0
): { startOffset: number; endOffset: number } {
  const along =
    (target.x - pivot.x) * dir.x +
    (target.z - pivot.z) * dir.z;

  return {
    startOffset: Math.max(
      startOffset,
      along < 0 ? Math.abs(along) + padding : padding
    ),
    endOffset: Math.max(
      endOffset,
      along > 0 ? along + padding : padding
    ),
  };
}

function buildScreenGuideLineThroughPivot(
  pivotX: number,
  pivotY: number,
  dirX: number,
  dirY: number,
  halfLengthPx: number
) {
  return {
    x1: pivotX - dirX * halfLengthPx,
    y1: pivotY - dirY * halfLengthPx,
    x2: pivotX + dirX * halfLengthPx,
    y2: pivotY + dirY * halfLengthPx,
  };
}

function getOffsetPoint(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  offsetPx: number
) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;

  // screen-space perpendicular
  const nx = -dy / len;
  const ny = dx / len;

  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;

  return {
    x: mx + nx * offsetPx,
    y: my + ny * offsetPx,
    lengthPx: len,
  };
}

function getSegmentLabelPosition(
  x1: number,
  y1: number,
  x2: number,
  y2: number
) {
  const baseOffset = 10;
  const { x, y, lengthPx } = getOffsetPoint(x1, y1, x2, y2, baseOffset);

  // If the segment is short, push the label farther away from the line
  if (lengthPx < 60) {
    const shortOffset = 18;
    const shortPos = getOffsetPoint(x1, y1, x2, y2, shortOffset);
    return { x: shortPos.x, y: shortPos.y };
  }

  return { x, y };
}

function getWidthLabelPosition(
  Bx: number,
  By: number,
  Fx: number,
  Fy: number,
  fallbackX: number,
  fallbackY: number
) {
  const dx = Fx - Bx;
  const dy = Fy - By;
  const len = Math.sqrt(dx * dx + dy * dy);

  // If the displayed perpendicular is too short, fallback
  if (!len || len < 18) {
    return { x: fallbackX, y: fallbackY };
  }

  // Push label off the width line so it doesn't sit on B/F/line
  const offsetPx = 14;
  const pos = getOffsetPoint(Bx, By, Fx, Fy, offsetPx);

  return { x: pos.x, y: pos.y };
}

const MeasureTool: React.FC<MeasureToolProps> = ({ viewerRef, isActive, onClose, onSave, onPointsChange, anchorRef, initialPoints }) => {
  const { t } = useI18n();
  const [step, setStep] = useState<MeasureStep>('idle');
  const [points, setPoints]     = useState<GeoPoint[]>([]);
  const [screenPts, setScreenPts] = useState<ScreenPos[]>([]);
  
  const stepRef   = useRef<MeasureStep>('idle');
  const rafRef    = useRef<number>(0);
  const pointsRef = useRef<GeoPoint[]>([]);

  const [isActualWidthMode, setIsActualWidthMode] = useState(false);
 
  
  const [guideDirection, setGuideDirection] = useState<WorldVectorXZ | null>(null);
  const [guideStartOffset, setGuideStartOffset] = useState<number>(4);
  const [guideEndOffset, setGuideEndOffset] = useState<number>(4);

 

  type LineAnalysisView = 'none' | 'slope' | 'width';

  const [lineAnalysisView, setLineAnalysisView] = useState<LineAnalysisView>('none');

  type DragHandle =
  | { kind: 'point'; index: number }
  | { kind: 'guide-start' }
  | { kind: 'guide-end' }
  | { kind: 'guide-line' }
  | null;

  const [draggingHandle, setDraggingHandle] = useState<DragHandle>(null);

  type PendingPointerIntent =
  | { kind: 'create-point' }
  | null;

  const pendingPointerIntentRef = useRef<PendingPointerIntent>(null);

  useEffect(() => { stepRef.current = step; }, [step]);
  useEffect(() => { pointsRef.current = points; }, [points]);
 

  // Centre the dropdown under the anchor button
  const [panelLeft, setPanelLeft] = React.useState<number | null>(null);
  useEffect(() => {
    if (!anchorRef?.current) return;
    const update = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPanelLeft(window.innerWidth - rect.right);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [anchorRef, isActive]);

    const reset = useCallback(() => {
      setPoints([]);
      setScreenPts([]);
      setStep('idle');
      setDraggingHandle(null);
      setIsActualWidthMode(false);
      setGuideDirection(null);
      setGuideStartOffset(4);
      setGuideEndOffset(4);
      
      setLineAnalysisView('none');
      pointsRef.current = [];
    }, []);

  useEffect(() => {
    if (!isActive) { reset(); return; }
    if (initialPoints && initialPoints.length > 0) {
      const viewer = viewerRef.current;
      const obs = viewer?.observerCoordinates;
      const geoPoints = initialPoints.map((wp) =>
        buildMeasuredPoint(wp, obs?.length >= 3 ? obs as [number, number, number] : null)
      );
      setPoints(geoPoints);
      pointsRef.current = geoPoints;
      setStep('done');
    } else {
      setStep('capturing');
    }
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // RAF loop to keep screen positions in sync with camera movement
  useEffect(() => {
    const tick = () => {
      const viewer = viewerRef.current;
      const canvas = viewer?.renderer?.domElement;
      const camera = viewer?.camera;
      if (canvas && camera && pointsRef.current.length > 0) {
        setScreenPts(pointsRef.current.map(p => projectToScreen(p.worldPos, camera, canvas)));
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [viewerRef]);

  // Click handler
  useEffect(() => {
      
      const canvas = viewerRef.current?.renderer?.domElement as HTMLCanvasElement | undefined;
      if (!canvas || !isActive) return;

      const blockViewerNavigation = (viewer: any) => {
        if (!viewer) return;
        viewer.clickTransitioning = false;
        viewer.transitioningCameraTarget = false;
        if (viewer.nextCamera) viewer.nextCamera.copy(viewer.camera);
        if (viewer.previousCamera) viewer.previousCamera.copy(viewer.camera);
      };

      const shouldBlockViewerNavigation = isActive;

      const syncViewerPointer = (viewer: any, event: PointerEvent) => {
        if (!viewer?.mousePosition?.set) return;
        const rect = canvas.getBoundingClientRect();
        viewer.mousePosition.set(
          event.clientX - rect.left,
          event.clientY - rect.top
        );
      };

      const onPointerDown = (event: PointerEvent) => {
        const viewer = viewerRef.current;
        syncViewerPointer(viewer, event);
        if (shouldBlockViewerNavigation) {
          event.preventDefault();
          event.stopImmediatePropagation();
          blockViewerNavigation(viewer);
        }
        if (draggingHandle) return;

        if (stepRef.current === 'capturing') {
          pendingPointerIntentRef.current = { kind: 'create-point' };
          return;
        }

        pendingPointerIntentRef.current = null;
      };

      const onPointerUp = (event: PointerEvent) => {
        const intent = pendingPointerIntentRef.current;
        pendingPointerIntentRef.current = null;

        const viewer = viewerRef.current;
        if (!viewer) return;
        syncViewerPointer(viewer, event);

        // stop navigation AFTER clickPosition was likely updated
        if (shouldBlockViewerNavigation) {
          event.preventDefault();
          event.stopImmediatePropagation();
          blockViewerNavigation(viewer);
        }

        if (intent?.kind !== 'create-point') return;
        if (stepRef.current !== 'capturing') return;

        setTimeout(() => {
          const wp = pickWorldPointFromPointer(
            viewer,
            canvas,
            event.clientX,
            event.clientY
          );
          if (!wp) return;

          if (shouldBlockViewerNavigation) {
            blockViewerNavigation(viewer);
          }

          const observerCoordinates = viewer.observerCoordinates;
          const hasObserverCoordinates = observerCoordinates && observerCoordinates.length >= 3;

          console.log('[MeasureTool] worldPos:', {
            x: wp.x.toFixed(4),
            y: wp.y.toFixed(4),
            z: wp.z.toFixed(4),
          });
          if (hasObserverCoordinates) {
            const measuredPoint = buildMeasuredPoint(
              wp,
              observerCoordinates as [number, number, number]
            );
            const _dbgMtm = latLonToMTM8(measuredPoint.latitude, measuredPoint.longitude);
            console.log('[MeasureTool] geodetic (pre-MTM8):', {
              lat: measuredPoint.latitude.toFixed(8),
              lon: measuredPoint.longitude.toFixed(8),
              alt: measuredPoint.altitude.toFixed(4),
            });
            console.log('[MeasureTool] GeoToWeb (MTM8):', {
              X: _dbgMtm.easting.toFixed(2),
              Y: _dbgMtm.northing.toFixed(2),
              Z: measuredPoint.altitude.toFixed(2),
            });
          }

          const newPoint = buildMeasuredPoint(
            { x: wp.x, y: wp.y, z: wp.z },
            hasObserverCoordinates
              ? observerCoordinates as [number, number, number]
              : null
          );

          const updated = [...pointsRef.current, newPoint];
          pointsRef.current = updated;
          setPoints(updated);
        }, 0);
      };

      canvas.addEventListener('pointerdown', onPointerDown, true);
      canvas.addEventListener('pointerup', onPointerUp, true);
      

      return () => {
        canvas.removeEventListener('pointerdown', onPointerDown, true);
        canvas.removeEventListener('pointerup', onPointerUp, true);
        
      };
  }, [viewerRef, draggingHandle, isActive]);

  // ── Auto-derived mode from point count ────────────────────────────────────
  const mode = points.length <= 1 ? 'point' : points.length === 2 ? 'line' : 'polygon';
  const usesWorldCoordinates = points.some(p => p.coordinateMode === 'world');
  const lineLength  = points.length >= 2
    ? usesWorldCoordinates
      ? totalLengthWorld3D(points)
      : totalLength(points)
    : null;
  useEffect(() => {
  if (mode !== 'line' || points.length !== 2) return;

  const worldMetrics = lineMetricsWorld(points[0], points[1]);

  console.log('----- Distance comparison -----');
  if (!usesWorldCoordinates) {
    const geoDist = segmentDistance(points[0], points[1]);
    console.log('Geodetic distance:', geoDist.toFixed(3), 'm');
  }
  console.log('World 3D distance:', worldMetrics.length.toFixed(3), 'm');
  console.log('World run:', worldMetrics.run.toFixed(3), 'm');
  console.log('World rise:', worldMetrics.rise.toFixed(3), 'm');
  console.log('Slope angle:', worldMetrics.angle.toFixed(3), '°');
}, [mode, points, usesWorldCoordinates]);
  // Area shown in the measuring UI is the projected ground-plane area.
  // polygonArea() remains available in measureUtils for true 3D surface area.
  const area        = points.length >= 3 ? polygonAreaXZ(points) : null;

  useEffect(() => {
    if (!onPointsChange) return;
    onPointsChange({
      points: points.map((p) => ({ x: p.worldPos.x, y: p.worldPos.y, z: p.worldPos.z })),
      mode,
      length: lineLength,
      area,
    });
  }, [points, mode, lineLength, area, onPointsChange]);

  const perimeter   = points.length >= 3
    ? usesWorldCoordinates
      ? totalLengthWorld3D([...points, points[0]])
      : totalLength([...points, points[0]])
    : null;

  const handleDone = useCallback(() => {
  setStep('done');
}, []);

  const isWidthMode = mode === 'line' && lineAnalysisView === 'width';
  const isSlopeMode = mode === 'line' && lineAnalysisView === 'slope';
  const effectiveMode = isWidthMode ? 'width' : mode;

  const shouldBlockScenePointerEvents =
  isWidthMode ||
  (mode === 'line' && isSlopeMode && step === 'done') ||
  (mode === 'polygon' && step === 'done');

  const actualWidthGeometry = useMemo(() => {
    if (!isActualWidthMode || points.length < 2 || !guideDirection) return null;

    const A = points[0];
    const B = points[1];

    const normalizedGuideDir = normalizeXZ(guideDirection);
    if (!normalizedGuideDir) return null;

    const fittedOffsets = ensureGuideCoversProjection(
      A.worldPos,
      B.worldPos,
      normalizedGuideDir,
      guideStartOffset,
      guideEndOffset,
      1.0 // padding in meters
    );

    const guide = buildGuideLine(
      A.worldPos,
      normalizedGuideDir,
      fittedOffsets.startOffset,
      fittedOffsets.endOffset
    );

    const projection = projectPointToLineXZ(
      B.worldPos,
      guide.start,
      guide.end,
      false
    );

    if (!projection) return null;

    const projectedPointOnGuide = {
      x: projection.projectedPoint.x,
      y: A.worldPos.y, // force onto guide plane
      z: projection.projectedPoint.z,
    };

    const correctedProjection = {
      ...projection,
      projectedPoint: projectedPointOnGuide,
    };

    return {
      pivot: A,
      target: B,
      baselineStart: guide.start,
      baselineEnd: guide.end,
      guideDirection: normalizedGuideDir,
      projection: correctedProjection,
      width: correctedProjection.perpendicularDistance,
      skewDistance: worldDistanceXZ(A.worldPos, B.worldPos),
      fittedStartOffset: fittedOffsets.startOffset,
      fittedEndOffset: fittedOffsets.endOffset,
    };
  }, [
    isActualWidthMode,
    points,
    guideDirection,
    guideStartOffset,
    guideEndOffset,
  ]);

  const pickedLineRepresentsWidth = useMemo(() => {
    if (!isWidthMode || !actualWidthGeometry) return false;

    const foot = actualWidthGeometry.projection.projectedPoint;
    const A = actualWidthGeometry.pivot.worldPos;

    return worldDistanceXZ(foot, A) < 0.15;
  }, [isWidthMode, actualWidthGeometry]);

  const slopeMetrics = useMemo(() => {
  if (mode !== 'line' || points.length !== 2) return null;

  const first = points[0];
  const last = points[1];

  const isFirstHigher = first.altitude >= last.altitude;
  const H = isFirstHigher ? first : last;
  const L = isFirstHigher ? last : first;

  const rise = Math.abs(H.worldPos.y - L.worldPos.y);
  const run = Math.sqrt(
    (H.worldPos.x - L.worldPos.x) ** 2 +
    (H.worldPos.z - L.worldPos.z) ** 2
  );
  const angle = run > 0 ? (Math.atan2(rise, run) * 180) / Math.PI : 0;

  return {
    H,
    L,
    rise,
    run,
    angle,
  };
}, [mode, points]);

  const handleSave = useCallback(() => {
      onSave?.({
        points,
        mode: effectiveMode,
        length: isWidthMode ? actualWidthGeometry?.width ?? null : lineLength,
        area: isWidthMode ? null : area ?? null,
      });
      reset();
      onClose();
    }, [
      points,
      effectiveMode,
      isWidthMode,
      actualWidthGeometry,
      lineLength,
      area,
      onSave,
      reset,
      onClose,
    ]);
  
  const clearWidthAnalysisState = useCallback(() => {
    setIsActualWidthMode(false);
    setGuideDirection(null);
    setGuideStartOffset(4);
    setGuideEndOffset(4);
    setDraggingHandle(null);
  }, []);

  const handleMeasureWidth = useCallback(() => {
    if (points.length !== 2) return;

    const A = points[0];
    const B = points[1];
    const rawLine = lineMetricsWorld(A, B);
    const frozenAB = normalizeXZ(vectorXZ(A.worldPos, B.worldPos));

    const turningOff = lineAnalysisView === 'width';

    if (turningOff) {
      clearWidthAnalysisState();
      setLineAnalysisView('none');
      setStep('done');
      return;
    }
    const viewer = viewerRef.current;
    const camera = viewer?.camera;

    let initialGuideDir = camera ? cameraForwardXZ(camera) : null;

    if (!initialGuideDir || Math.abs(initialGuideDir.x) + Math.abs(initialGuideDir.z) < 0.001) {
      initialGuideDir = { x: 1, z: 0 };
    } 


const initialExtent = Math.max(2.5, Math.min(12, rawLine.length * 0.75));
   
    setLineAnalysisView('width');
    setIsActualWidthMode(true);
    
    
    setGuideDirection(initialGuideDir);
    setGuideStartOffset(initialExtent);
    setGuideEndOffset(initialExtent);
    setDraggingHandle(null);
    setStep('done');

    console.log('----- True width mode -----');
    console.log('Picked A:', A.worldPos);
    console.log('Picked B:', B.worldPos);
    console.log('Frozen AB:', frozenAB);
    console.log('Raw picked distance:', rawLine.length.toFixed(3), 'm');
  }, [points, lineAnalysisView, clearWidthAnalysisState]);

  const handleCancel = useCallback(() => {
  setPoints([]);
  setScreenPts([]);
  setIsActualWidthMode(false);
  setGuideDirection(null);
  setGuideStartOffset(4);
  setGuideEndOffset(4);
  
  setLineAnalysisView('none');
 
  setDraggingHandle(null);
  pointsRef.current = [];
  setStep('capturing');
}, []);

function updatePointAtIndex(
  pts: GeoPoint[],
  index: number,
  next: GeoPoint
): GeoPoint[] {
  return pts.map((p, i) => (i === index ? next : p));
}

useEffect(() => {
  if (!draggingHandle) return;

  const viewer = viewerRef.current;
  const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
  const camera = viewer?.camera;
  if (!canvas || !camera) return;

  const onPointerMove = (e: PointerEvent) => {
    const pts = pointsRef.current;
    if (!pts.length) return;

    // ---------- 1. DRAG NORMAL POINTS ----------
    if (draggingHandle.kind === 'point') {
      const index = draggingHandle.index;
      const point = pts[index];
      if (!point) return;

      const anchorY = point.worldPos.y;

      const hit = screenToWorldOnPlane(
        e.clientX,
        e.clientY,
        camera,
        canvas,
        anchorY
      );
      if (!hit) return;

      const observerCoordinates =
        viewer?.observerCoordinates && viewer.observerCoordinates.length >= 3
          ? viewer.observerCoordinates as [number, number, number]
          : null;

      // ---------- WIDTH MODE SPECIAL CASE FOR B ----------
      const isConstrainedWidthBDrag =
        index === 1 &&
        isWidthMode &&
        actualWidthGeometry &&
        actualWidthGeometry.projection;

      if (isConstrainedWidthBDrag) {
        const foot = actualWidthGeometry.projection.projectedPoint;
        const currentB = actualWidthGeometry.target.worldPos;

        const perpDir = normalizeXZ({
          x: currentB.x - foot.x,
          z: currentB.z - foot.z,
        });
        if (!perpDir) return;

        const dx = hit.x - foot.x;
        const dz = hit.z - foot.z;

        const signedDist = dx * perpDir.x + dz * perpDir.z;

        const constrainedB = {
          x: foot.x + signedDist * perpDir.x,
          y: anchorY,
          z: foot.z + signedDist * perpDir.z,
        };

        const updated = updatePointAtIndex(
          pts,
          index,
          buildMeasuredPoint(constrainedB, observerCoordinates)
        );

        pointsRef.current = updated;
        setPoints(updated);
        return;
      }

      // ---------- NORMAL POINT DRAG ----------
      const updated = updatePointAtIndex(
        pts,
        index,
        buildMeasuredPoint({ x: hit.x, y: hit.y, z: hit.z }, observerCoordinates)
      );

      pointsRef.current = updated;
      setPoints(updated);
      return;
    }

  
    // ---------- 2. GUIDE HANDLE DRAG ----------
      if (
      (draggingHandle.kind === 'guide-start' ||
        draggingHandle.kind === 'guide-end' ||
        draggingHandle.kind === 'guide-line') &&
      isActualWidthMode &&
      actualWidthGeometry
      ) {
      const pivot = actualWidthGeometry.pivot.worldPos;
      const planeY = pivot.y;

      const hit = screenToWorldOnPlane(
        e.clientX,
        e.clientY,
        camera,
        canvas,
        planeY
      );
      if (!hit) return;

      let draggedVec: { x: number; z: number };

      if (draggingHandle.kind === 'guide-start') {
        draggedVec = {
          x: pivot.x - hit.x,
          z: pivot.z - hit.z,
        };
      } else {
        // guide-end and guide-line both orient from pivot toward pointer
        draggedVec = {
          x: hit.x - pivot.x,
          z: hit.z - pivot.z,
        };
      }

      const nextDir = normalizeXZ(draggedVec);
      if (!nextDir) return;

      
      setGuideDirection(nextDir);

      // Only resize extents when dragging actual endpoints
      if (draggingHandle.kind === 'guide-start' || draggingHandle.kind === 'guide-end') {
        const nextLen = Math.sqrt(
          draggedVec.x * draggedVec.x + draggedVec.z * draggedVec.z
        );

        if (draggingHandle.kind === 'guide-start') {
          setGuideStartOffset(Math.max(1.5, nextLen));
        } else {
          setGuideEndOffset(Math.max(1.5, nextLen));
      }
    }
      return;
    }
  };

  const onPointerUp = () => {
    pendingPointerIntentRef.current = null;
    setDraggingHandle(null);
  };

  const onPointerCancel = () => {
    pendingPointerIntentRef.current = null;
    setDraggingHandle(null);
  };

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  window.addEventListener('pointercancel', onPointerCancel);

  return () => {
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    window.removeEventListener('pointercancel', onPointerCancel);
  };
}, [
  draggingHandle,
  viewerRef,
  isWidthMode,
  isActualWidthMode,
  actualWidthGeometry,
  guideDirection,
]);

  if (!isActive) return null;

  const modeIcon =
  effectiveMode === 'point' ? (
    <svg width="10" height="10" viewBox="0 0 8 8">
      <circle cx="4" cy="4" r="3.5" fill="white" />
    </svg>
  ) : effectiveMode === 'line' ? (
    <svg width="14" height="8" viewBox="0 0 14 8">
      <line x1="1" y1="4" x2="13" y2="4" stroke="white" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ) : effectiveMode === 'width' ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="8 9 5 12 8 15" />
      <polyline points="16 9 19 12 16 15" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 19.5 8 17 17 7 17 4.5 8" />
    </svg>
  );

  const modeLabel =
  effectiveMode === 'width'
    ? t.measure.modeWidth
    : effectiveMode === 'point'
    ? t.measure.modePoint
    : effectiveMode === 'line'
    ? t.measure.modeLine
    : t.measure.modePolygon;

  return (
    <>
      {/* Crosshair cursor while capturing */}
      {step === 'capturing' && (
        <style>{`canvas { cursor: crosshair !important; }`}</style>
      )}

      {/* Instruction toast 
      {step === 'capturing' && (
        <div className="fixed z-[1002] px-4 py-2 rounded-full pointer-events-none"
            style={{
            top: 80,
            right: (panelLeft ?? 16) + 256 + 15,
            background: 'rgba(0,0,0,0.35)',
            backdropFilter: 'blur(6px)',
            color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: 400,
            whiteSpace: 'nowrap', letterSpacing: '0.02em',
        }}>
          📍 {instructionText}
        </div>
      )}
        */}
      {/* ── SVG overlay: lines + polygon fill ── */}
      {screenPts.length >= 2 && (
        <svg
          className="fixed inset-0 z-[1000]"
          style={{
            width: '100vw',
            height: '100vh',
            pointerEvents: shouldBlockScenePointerEvents ? 'auto' : 'none',
          }}
        >
          {shouldBlockScenePointerEvents && !isWidthMode && (
            <rect
              x="0"
              y="0"
              width="100%"
              height="100%"
              fill="transparent"
              pointerEvents="auto"
            />
          )}
          {/* Polygon fill when done */}
          {mode === 'polygon' && step === 'done' && !isWidthMode && (
            <polygon
              points={screenPts.filter(s => s.visible).map(s => `${s.x},${s.y}`).join(' ')}
              fill="rgba(253,186,89,0.12)"
              stroke="none"
            />
          )}
          {/* Segment lines with distance labels */}
          {!isWidthMode && screenPts.map((sp, i) => {
            if (i === 0) return null;

            const prev = screenPts[i - 1];
            if (!sp.visible || !prev.visible) return null;

            const d = usesWorldCoordinates
              ? worldDistance3D(points[i - 1].worldPos, points[i].worldPos)
              : segmentDistance(points[i - 1], points[i]);
            const labelPos = getSegmentLabelPosition(
              prev.x,
              prev.y,
              sp.x,
              sp.y
            );
            return (
              <g key={i}>
                <line
                  x1={prev.x}
                  y1={prev.y}
                  x2={sp.x}
                  y2={sp.y}
                  stroke="white"
                  strokeWidth="2"
                  strokeDasharray="6 4"
                  strokeLinecap="round"
                />
                <text
                  x={labelPos.x}
                  y={labelPos.y}
                  textAnchor="middle"
                  fill="white"
                  fontSize="11"
                  fontWeight="bold"
                  fontFamily="Arial"
                  style={{
                    pointerEvents: 'none'
                  }}
                >
                  {fmtDist(d)}
                </text>
              </g>
            );
          })}
          {/* Closing line for polygon */}
          {mode === 'polygon' && step === 'done' && screenPts.length >= 3 && !isWidthMode && (() => {
            const f = screenPts[0], l = screenPts[screenPts.length - 1];
            if (!f.visible || !l.visible) return null;
            const d = usesWorldCoordinates
              ? worldDistance3D(points[points.length - 1].worldPos, points[0].worldPos)
              : segmentDistance(points[points.length - 1], points[0]);
            const closingLabelPos = getSegmentLabelPosition(
              l.x,
              l.y,
              f.x,
              f.y
            );
            return (
                <g>
                <line x1={l.x} y1={l.y} x2={f.x} y2={f.y}
                    stroke="white" strokeWidth="2" strokeDasharray="6 4" strokeLinecap="round" />
                <text
                  x={closingLabelPos.x}
                  y={closingLabelPos.y}
                  textAnchor="middle"
                  fill="white"
                  fontSize="11"
                  fontWeight="bold"
                  fontFamily="Arial"
                  style={{
                    pointerEvents: 'none',
                    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.9))'
                  }}
                >
                  {fmtDist(d)}
                </text>
                </g>
            );
            })()}
            {/* Width measurement overlay */}
            {isWidthMode && actualWidthGeometry && (() => {
              const viewer = viewerRef.current;
              const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
              const camera = viewer?.camera;
              if (!canvas || !camera) return null;

              const Asc = projectToScreen(actualWidthGeometry.pivot.worldPos, camera, canvas);
              const Bsc = projectToScreen(actualWidthGeometry.target.worldPos, camera, canvas);
              const A1sc = projectToScreen(actualWidthGeometry.baselineStart, camera, canvas);
              const A2sc = projectToScreen(actualWidthGeometry.baselineEnd, camera, canvas);
              const Fsc = projectToScreen(actualWidthGeometry.projection.projectedPoint, camera, canvas);

              if (!Asc.visible || !Bsc.visible) return null;

              const showGuide = A1sc.visible && A2sc.visible;
              const isDraggingAnyPoint = draggingHandle?.kind === 'point';
              const showHandles = A1sc.visible && A2sc.visible && !isDraggingAnyPoint;
              const showGuideHint = showGuide && !isDraggingAnyPoint;
              const showFoot = Fsc.visible;
              const showPerp = !pickedLineRepresentsWidth && Fsc.visible;

              let guideLineScreen:
                | { x1: number; y1: number; x2: number; y2: number }
                | null = null;
              


              // Preferred: use actual projected guide endpoints
              if (A1sc.visible && A2sc.visible) {
                guideLineScreen = {
                  x1: A1sc.x,
                  y1: A1sc.y,
                  x2: A2sc.x,
                  y2: A2sc.y,
                };
              } else if (Asc.visible && actualWidthGeometry.guideDirection) {
                // Fallback: draw a screen-space guide through A using guide direction
                const dir = actualWidthGeometry.guideDirection;

                const probeWorld = {
                  x: actualWidthGeometry.pivot.worldPos.x + dir.x * 2,
                  y: actualWidthGeometry.pivot.worldPos.y,
                  z: actualWidthGeometry.pivot.worldPos.z + dir.z * 2,
                };

                const probeSc = projectToScreen(probeWorld, camera, canvas);

                if (probeSc.visible) {
                  const dx = probeSc.x - Asc.x;
                  const dy = probeSc.y - Asc.y;
                  const len = Math.sqrt(dx * dx + dy * dy);

                  if (len > 0.001) {
                    const ux = dx / len;
                    const uy = dy / len;

                    guideLineScreen = buildScreenGuideLineThroughPivot(
                      Asc.x,
                      Asc.y,
                      ux,
                      uy,
                      220
                    );
                  }
                }
              }
              const widthLabelPos = showPerp
                ? getWidthLabelPosition(
                    Bsc.x,
                    Bsc.y,
                    Fsc.x,
                    Fsc.y,
                    (Asc.x + Bsc.x) / 2,
                    (Asc.y + Bsc.y) / 2 - 12
                  )
                : {
                    x: (Asc.x + Bsc.x) / 2,
                    y: (Asc.y + Bsc.y) / 2 - 12,
                };

              const labelStyle = {
                pointerEvents: 'none' as const,
                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.95))',
              };

              console.log('[Width overlay]', {
                hasGeometry: !!actualWidthGeometry,
                A1Visible: A1sc.visible,
                A2Visible: A2sc.visible,
                AscVisible: Asc.visible,
                BscVisible: Bsc.visible,
                FscVisible: Fsc.visible,
                width: actualWidthGeometry.width,
              });
              const hasRealGuideHandles = A1sc.visible && A2sc.visible;
              const isUsingFallbackGuideLine = !!guideLineScreen && !hasRealGuideHandles;
              
              return (
                <g>
                  {/* Adjustable guide line A' - A - A" */}
                  {guideLineScreen && (
                    <>
                      {/* visible guide line */}
                      <line
                        x1={guideLineScreen.x1}
                        y1={guideLineScreen.y1}
                        x2={guideLineScreen.x2}
                        y2={guideLineScreen.y2}
                        stroke="#0CB4E4"
                        strokeWidth="2"
                        strokeDasharray="7 4"
                        strokeLinecap="round"
                        pointerEvents="none"
                      />

                      {/* fallback drag target when A' / A" are offscreen */}
                      {isUsingFallbackGuideLine && (
                        <line
                          x1={guideLineScreen.x1}
                          y1={guideLineScreen.y1}
                          x2={guideLineScreen.x2}
                          y2={guideLineScreen.y2}
                          stroke="transparent"
                          strokeWidth="24"
                          strokeLinecap="round"
                          style={{
                            pointerEvents: 'auto',
                            cursor: draggingHandle?.kind === 'guide-line' ? 'grabbing' : 'grab',
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            (e.currentTarget as SVGLineElement).setPointerCapture?.(e.pointerId);
                            setDraggingHandle({ kind: 'guide-line' });
                          }}
                        />
                      )}
                    </>
                  )}
                  {showGuideHint && (
                    <text
                      x={A2sc.x + 8}
                      y={A2sc.y - 8}
                      fill="rgba(255,255,255,0.78)"
                      fontSize="10"
                      fontWeight="600"
                      fontFamily="Arial"
                      style={labelStyle}
                    >
                      {t.measure.widthGuideHint}
                    </text>
                  )}
                  {/* Original picked AB line */}
                    <line
                      x1={Asc.x}
                      y1={Asc.y}
                      x2={Bsc.x}
                      y2={Bsc.y}
                      stroke={pickedLineRepresentsWidth ? '#0CB4E4' : 'white'}
                      strokeWidth={pickedLineRepresentsWidth ? 2.5 : 2}
                      strokeDasharray={pickedLineRepresentsWidth ? '' : '4 4'}
                      pointerEvents="none"
                    />

                  {/* Little guide end caps */}
                    
                    {showHandles && (
                      <>
                        {/* A' draggable handle */}
                        <circle
                          cx={A1sc.x}
                          cy={A1sc.y}
                          r="8"
                          fill="rgba(34,197,94,0.18)"
                          stroke="rgba(255,255,255,0.9)"
                          strokeWidth="1.5"
                          style={{
                            pointerEvents: 'auto',
                            cursor: draggingHandle?.kind === 'guide-start' ? 'grabbing' : 'grab'
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            (e.currentTarget as SVGCircleElement).setPointerCapture?.(e.pointerId);
                             setDraggingHandle({ kind: 'guide-start' })
                          }}
                        />
                        <text
                          x={A1sc.x}
                          y={A1sc.y - 12}
                          textAnchor="middle"
                          fill="#0CB4E4"
                          fontSize="11"
                          fontWeight="700"
                          fontFamily="Arial"
                          style={{ pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.95))' }}
                        >
                          A'
                        </text>

                        {/* A" draggable handle */}
                        <circle
                          cx={A2sc.x}
                          cy={A2sc.y}
                          r="8"
                          fill="rgba(34,197,94,0.18)"
                          stroke="rgba(255,255,255,0.9)"
                          strokeWidth="1.5"
                          style={{
                            pointerEvents: 'auto',
                            cursor: draggingHandle?.kind === 'guide-end' ? 'grabbing' : 'grab'
                          }}
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            (e.currentTarget as SVGCircleElement).setPointerCapture?.(e.pointerId);
                            setDraggingHandle({ kind: 'guide-end' })
                          }}
                        />
                        <text
                          x={A2sc.x}
                          y={A2sc.y - 12}
                          textAnchor="middle"
                          fill="#0CB4E4"
                          fontSize="11"
                          fontWeight="700"
                          fontFamily="Arial"
                          style={{ pointerEvents: 'none', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.95))' }}
                        >
                          A"
                        </text>
                      </>
                    )}

                  {/* Width line from B to foot point */}
                  {showPerp && (
                    <line
                      x1={Bsc.x}
                      y1={Bsc.y}
                      x2={Fsc.x}
                      y2={Fsc.y}
                      stroke="#0CB4E4"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                    />
                  )}

                  {/* Foot point */}
                  {showFoot && !pickedLineRepresentsWidth && (
                    <circle
                      cx={Fsc.x}
                      cy={Fsc.y}
                      r="4"
                      fill="#0CB4E4"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                  )}

                  {/* Right angle cue */}
                  {showPerp && (() => {
                    const vx = Bsc.x - Fsc.x;
                    const vy = Bsc.y - Fsc.y;
                    const vlen = Math.sqrt(vx * vx + vy * vy);
                    if (vlen < 12) return null;

                    const ux = vx / vlen;
                    const uy = vy / vlen;

                    const bx = A2sc.x - A1sc.x;
                    const by = A2sc.y - A1sc.y;
                    const blen = Math.sqrt(bx * bx + by * by);
                    if (blen < 12) return null;

                    const bux = bx / blen;
                    const buy = by / blen;
                    const s = 10;
                    return (
                      <path
                        d={`M ${Fsc.x + bux * s} ${Fsc.y + buy * s}
                            L ${Fsc.x + bux * s + ux * s} ${Fsc.y + buy * s + uy * s}
                            L ${Fsc.x + ux * s} ${Fsc.y + uy * s}`}
                        fill="none"
                        stroke="rgba(253,186,89,0.9)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    );
                  })()}

                  {/* Width label */}
                  
                  <text
                    x={widthLabelPos.x}
                    y={widthLabelPos.y}
                    textAnchor="middle"
                    fill="white"
                    fontSize="12"
                    fontWeight="700"
                    fontFamily="Arial"
                    style={labelStyle}
                  >
                    {fmtDist(actualWidthGeometry.width)}
                  </text>

                 
                </g>
              );
            })()}
            {/* Slope triangle for line mode */}
            {mode === 'line' && isSlopeMode && step === 'done' && screenPts.length >= 2 && slopeMetrics && (() => {
              const viewer = viewerRef.current;
              const canvas = viewer?.renderer?.domElement as HTMLCanvasElement | undefined;
              const camera = viewer?.camera;
              if (!canvas || !camera) return null;

              const first = points[0];
              const last = points[1];
              const isFirstHigher = first.altitude >= last.altitude;

              const Hsc = isFirstHigher ? screenPts[0] : screenPts[1];
              const Lsc = isFirstHigher ? screenPts[1] : screenPts[0];
              if (!Hsc.visible || !Lsc.visible) return null;

              if (slopeMetrics.rise < 0.05) return null;

              const Cworld = {
                x: slopeMetrics.H.worldPos.x,
                y: slopeMetrics.L.worldPos.y,
                z: slopeMetrics.H.worldPos.z,
              };

              const Csc = projectToScreen(Cworld, camera, canvas);
              if (!Csc.visible) return null;

              const labelStyle = {
                filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.95))',
              };

              return (
                <g>
                  <polygon
                    points={`${Hsc.x},${Hsc.y} ${Lsc.x},${Lsc.y} ${Csc.x},${Csc.y}`}
                    fill="rgba(253,186,89,0.08)"
                    stroke="rgba(253,186,89,0.5)"
                    strokeWidth="1.5"
                    strokeDasharray="5 3"
                  />

                  <text
                    x={Lsc.x + (Csc.x - Lsc.x) * 0.22}
                    y={Lsc.y + (Csc.y - Lsc.y) * 0.22 - 10}
                    fill="white"
                    fontSize="12"
                    fontWeight="700"
                    fontFamily="Arial"
                    style={labelStyle}
                  >
                    {slopeMetrics.angle.toFixed(1)}°
                  </text>

                  <text
                    x={(Hsc.x + Csc.x) / 2 + 10}
                    y={(Hsc.y + Csc.y) / 2}
                    textAnchor="start"
                    fill="rgba(255,255,255,0.78)"
                    fontSize="10"
                    fontWeight="600"
                    fontFamily="Arial"
                    style={labelStyle}
                  >
                    {t.measure.slopeRise} {slopeMetrics.rise.toFixed(2)}m
                  </text>

                  <text
                    x={(Csc.x + Lsc.x) / 2}
                    y={(Csc.y + Lsc.y) / 2 + 14}
                    textAnchor="middle"
                    fill="rgba(255,255,255,0.72)"
                    fontSize="10"
                    fontWeight="600"
                    fontFamily="Arial"
                    style={labelStyle}
                  >
                    {t.measure.slopeRun} {fmtDist(slopeMetrics.run)}
                  </text>
                </g>
              );
            })()}
          
        </svg>
      )}

      {/* Marker dots */}
      {screenPts.map((sp, i) => {
        {/* Draggable B handle when a 2-point measurement is done */}
          
        if (!sp.visible) return null;
        return (
          <div key={i} className="fixed z-[1001] pointer-events-none"
            style={{ left: sp.x - 14, top: sp.y - 14 }}>
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: markerColor(i),
              boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontWeight: 700, fontSize: 11, fontFamily: 'Arial',
            }}>{markerLabel(i)}</div>
          </div>
        );
      })}

      {screenPts.map((sp, i) => {
        if (!sp.visible) return null;

        return (
          <div
            key={`drag-point-${i}`}
            className="fixed z-[1003]"
            style={{
              left: sp.x - 12,
              top: sp.y - 12,
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'transparent',
              boxShadow: '0 2px 8px rgba(0,0,0,0.45)',
              pointerEvents: 'auto',
              cursor:
                draggingHandle?.kind === 'point' &&
                draggingHandle.index === i
                  ? 'grabbing'
                  : 'grab',
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              pendingPointerIntentRef.current = null;
              setDraggingHandle({ kind: 'point', index: i });
            }}
          />
        );
      })}

     {/* ── Dropdown panel ── */}
      <div className="fixed z-[1002] w-64 rounded-2xl overflow-hidden pointer-events-none"
        style={{
          top: 12,
          ...(panelLeft !== null
            ? { right: panelLeft }
            : { right: 76 }),
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.12)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.45)',
        }}>

        {/* Header: auto-mode badge + close button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10" style={{ pointerEvents: 'auto' }}>
          <div className="flex items-center gap-2">
            {modeIcon}
            <span style={{ color: 'white', fontSize: 13, fontWeight: 600 }}>{modeLabel}</span>
          </div>
          <button
            onClick={onClose}
            style={{ color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', padding: 4, borderRadius: 6, lineHeight: 1 }}
            onMouseEnter={e => (e.currentTarget.style.color = 'white')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Stats — shown at top when done */}
        {step === 'done' && (
          <div className="px-3 pt-3 pb-1 space-y-2 border-b border-white/10" style={{ pointerEvents: 'auto' }}>
            
            {effectiveMode === 'line' && lineLength !== null && (
              <div className="rounded-xl px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  {t.measure.statLength}
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: 'white' }}>
                  {fmtDist(lineLength)}
                </div>

                {isSlopeMode && slopeMetrics && (
                  slopeMetrics.rise < 0.05 ? (
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 8 }}>
                      {t.measure.statNoElevation}
                    </div>
                  ) : (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 8 }}>
                        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '6px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginBottom: 2 }}>{t.measure.statSlope}</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: 'white', lineHeight: 1 }}>
                            {slopeMetrics.angle.toFixed(1)}°
                          </div>
                        </div>
                        <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '6px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)', marginBottom: 2 }}>{t.measure.slopeGrade}</div>
                          <div style={{ fontSize: 16, fontWeight: 700, color: 'white', lineHeight: 1 }}>
                            {slopeMetrics.run > 0.01 ? `${((slopeMetrics.rise / slopeMetrics.run) * 100).toFixed(1)}%` : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}

            {effectiveMode === 'point' && points.length === 1 && (
              <div className="rounded-xl px-3 py-2 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 2 }}>
                  {t.measure.statCoordinates}
                </div>
                <div style={{ fontSize: 11, color: 'white', fontFamily: 'monospace' }}>
                  {(() => {
                    if (points[0].coordinateMode === 'world') {
                      const wp = points[0].worldPos;
                      return <>{wp.x.toFixed(2)}, {wp.y.toFixed(2)}, {wp.z.toFixed(2)}</>;
                    }
                    const mtm = latLonToMTM8(points[0].latitude, points[0].longitude);
                    const wp = points[0].worldPos;
                    return (
                      <>
                        <div>Map: {fmtUTM(mtm.easting)}, {fmtUTM(mtm.northing)}, {points[0].altitude.toFixed(2)}</div>
                        <div>Viewer: {wp.x.toFixed(2)}, {wp.y.toFixed(2)}, {wp.z.toFixed(2)}</div>
                      </>
                    );
                  })()}
                </div>
              </div>
            )}

            {effectiveMode === 'width' && pickedLineRepresentsWidth && (
                <div
                  className="rounded-xl px-3 py-2"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.82)',
                    fontSize: 11,
                    lineHeight: 1.5,
                  }}
                >
                  {t.measure.widthPickedLineNote}
                </div>
            )}

            {effectiveMode === 'width' && actualWidthGeometry && (
              <div className="rounded-xl px-3 py-2" style={{ background: 'rgba(255,255,255,0.06)' }}>
                <div
                  style={{
                    fontSize: 9,
                    color: 'rgba(255,255,255,0.4)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    marginBottom: 2,
                  }}
                >
                  {t.measure.statPerpWidth}
                </div>

                <div
                  style={{
                    fontSize: 20,
                    fontWeight: 700,
                    color: 'white',
                  }}
                >
                  {fmtDist(actualWidthGeometry.width)}
                </div>

                <div
                  style={{
                    marginTop: 8,
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.6)',
                    fontFamily: 'monospace',
                    lineHeight: 1.7,
                    textAlign: 'left',
                  }}
                >
                <div>{t.measure.widthDescription}</div>
                </div>
              </div>
            )}

            {effectiveMode === 'width' && !actualWidthGeometry && (
              <div
                className="rounded-xl px-3 py-2"
                style={{
                  background: 'rgba(239,68,68,0.08)',
                  color: 'rgba(255,255,255,0.82)',
                  fontSize: 11,
                  lineHeight: 1.5,
                }}
              >
                {t.measure.widthComputeError}
              </div>
            )}

            {effectiveMode === 'polygon' && perimeter !== null && area !== null && (
              <div>
                <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 6 }}>
                  <div className="rounded-xl px-2 py-2 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                      {t.measure.statPerimeter}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{fmtDist(perimeter)}</div>
                  </div>
                  <div className="rounded-xl px-2 py-2 text-center" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 2 }}>
                      {t.measure.statArea}
                    </div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'white' }}>{fmtArea(area)}</div>
                  </div>
                </div>
              </div>
            )}

          </div>
        )}

        {/* Points list */}

        
        <div className="p-3 space-y-2" style={{ maxHeight: 240, overflowY: 'auto' , pointerEvents: 'auto' }}>
          {points.length === 0 && (
            <div style={{ fontSize: 12, color: 'white', textAlign: 'center', padding: '8px 0' }}>
              {t.measure.clickToPlace}
            </div>
          )}
          {points.map((p, i) => (
            <div key={i} className="flex items-start gap-2">
              <div className="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-white mt-0.5"
                style={{ background: markerColor(i), fontSize: 9, fontWeight: 700 }}>
                {markerLabel(i)}
              </div>
              <div style={{ fontSize: 10, fontFamily: 'monospace', lineHeight: 1.7, color: 'rgba(255,255,255,0.55)' }}>
                {(() => {
                  if (p.coordinateMode === 'world') {
                    return (<>
                      <div>X: {p.worldPos.x.toFixed(2)}</div>
                      <div>Y: {p.worldPos.y.toFixed(2)}</div>
                      <div>Z: {p.worldPos.z.toFixed(2)}</div>
                    </>);
                  }
                  const mtm = latLonToMTM8(p.latitude, p.longitude);
                  return (<>
                    <div>Map X: {fmtUTM(mtm.easting)}</div>
                    <div>Map Y: {fmtUTM(mtm.northing)}</div>
                    <div>Map Z: {p.altitude.toFixed(2)}</div>
                    <div style={{ marginTop: 4 }}>Viewer X: {p.worldPos.x.toFixed(2)}</div>
                    <div>Viewer Y: {p.worldPos.y.toFixed(2)}</div>
                    <div>Viewer Z: {p.worldPos.z.toFixed(2)}</div>
                  </>);
                })()}
              </div>
            </div>
          ))}  
        </div>


        {/* Footer */}
        <div className="flex flex-col gap-2 px-4 pb-4" style={{ pointerEvents: 'auto' }}>

          {step === 'capturing' && mode === 'polygon' && points.length >= 3 && (
            <div className="flex gap-2">

              <button
                onClick={handleDone}
               className="flex-1 py-2 rounded-xl text-xs font-semibold"
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                {t.measure.btnCompletePolygon}
              </button>
            </div>
          )}

          {mode === 'line' && points.length >= 2 && (
            <div className="flex gap-2">
              <button
                onClick={() => {
                  const turningOff = lineAnalysisView === 'slope';

                  clearWidthAnalysisState();
                  setLineAnalysisView(turningOff ? 'none' : 'slope');
                  setStep('done');
                }}
                className="flex-1 py-2 rounded-xl text-xs font-semibold"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                {t.measure.btnAnalyzeHeight}
              </button>

              <button
                onClick={handleMeasureWidth}
                className="flex-1 py-2 rounded-xl text-xs font-semibold"
                style={{
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                {t.measure.btnAnalyzeWidth}
              </button>
            </div>
          )}
          

          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.55)',
                cursor: 'pointer',
              }}
            >
              {t.measure.btnClearAll}
            </button>

            <button
              onClick={() => { reset(); onClose(); }}
              className="flex-1 py-2 rounded-xl text-xs font-semibold transition-all"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.55)',
                cursor: 'pointer',
              }}
            >
              {t.measure.btnDone}
            </button>
          </div>

        </div>
        
      </div>
    </>
  );
};

export default MeasureTool;
